import ms from 'ms';
import qs from 'querystring';
import Http from 'services/Http';
import computeContentMd5Base64 from 'utils/computeContentMd5Base64';
import createForm from 'utils/createForm';
import parseFileName from 'utils/getFileExtension';

class Employee {
  createForm = employee => {
    const ret = createForm(employee, {
      firstName: 'string',
      surName: 'string',
      jobTitle: 'string',
      department: 'string',
      email: 'string',
      phone: 'string',
      location: 'string',
      tags: 'string',
      code: 'string',
      id: 'string',
      reportsTo: 'string',
      channels: channels =>
        channels.map(({ _id, isAdmin, canWriteAsChannel }) => ({
          _id,
          isAdmin,
          canWriteAsChannel,
        })) || employee.channels_ids,
      channelsRemoved: 'array',
      teams: 'array',
      teamsRemoved: 'array',
      isAdmin: 'bool',
      isActive: 'bool',
      division: 'string',
      notes: 'string',
    });
    return ret;
  };

  async load(id) {
    const { data } = await Http.get(`/v1/employee/${id}`);
    return data.employee;
  }

  async mandatories(id, params) {
    const { data } = await Http.get(
      `/v1/employee/${id}/acknowledge/history?${qs.stringify(params)}`,
    );
    return data;
  }

  async destroy(id) {
    const response = await Http.delete(`/v1/employee/${id}`);

    return response.data;
  }

  async create(employee) {
    const form = this.createForm(employee);
    const response = await Http.post('/v1/employee', form);

    return response.data.employee;
  }

  async update(id, employee) {
    const form = this.createForm(employee);
    const response = await Http.patch(`/v1/employee/${id}`, form);

    return response.data.employee;
  }

  async previewChannels(id = 'new', data) {
    const form = this.createForm(data);
    const response = await Http.patch(`/v1/employee/${id}/channels-preview`, form);

    return response.data.channels;
  }

  async block(_id, isActive) {
    const response = await Http.patch(`/v1/employee/${_id}/block/${isActive}`);
    return response.data.isActive;
  }

  createImport(file) {
    const form = new FormData();
    form.append('file', file);
    return Http.post(`/v1/employee/import`, form, { timeout: ms('5m') });
  }

  async uploadAvatar(id, avatar) {
    const { type: mimeType, name } = avatar;
    const { ext } = parseFileName(name);
    const contentMd5 = await computeContentMd5Base64(avatar);
    try {
      const { data } = await Http.post(`/v1/employee/${id}/avatar/sign-url`, {
        mimeType,
        ext,
        contentMd5,
      });
      await Http.putS3(data.url || data.uploadUrl, avatar, {
        headers: { 'Content-Type': mimeType, 'Content-MD5': contentMd5, ...(data.headers || {}) },
        timeout: 0,
      });
      const uploaded = await Http.patch(`/v1/employee/${id}/avatar/uploaded`, { id: data.id });
      return uploaded.data.employee;
    } catch (err) {
      if (err.status === 409) {
        const form = new FormData();
        form.append('avatar', avatar);
        const { data } = await Http.post(`/v1/employee/${id}/file`, form);
        return data.employee;
      }
      throw err;
    }
  }

  async listAdmins() {
    const { data } = await Http.get(`/v1/employee/admins`);
    return data.employees;
  }

  massiveInvite(employeeIds) {
    return Http.post(`/v1/employee/upload`, { employeeIds });
  }

  async sync(companyId, since) {
    const body = since ? { since } : {};
    const { data } = await Http.post(`/v1/company/${companyId}/employee/sync`, body);
    return data;
  }
}

export default new Employee();
